import {patchState, signalStore, withComputed, withMethods, withState} from "@ngrx/signals";
import {withDevtools} from "@angular-architects/ngrx-toolkit";
import {computed, inject, linkedSignal} from "@angular/core";
import {ApiService} from "../../../shared/services/api.service";
import {take} from "rxjs";
import {getEndOfWeek, getMonthsBetweenDates, getNextMonthEndDate, getPreviousMonthStartDate, getStartOfWeek, getYearEndDate, getYearStartDate} from "../mapper/calendar-helper.mapper";
import {EventSettingsModel, Timezone, View, WorkHoursModel} from "@syncfusion/ej2-angular-schedule";
import {LocaleService} from "../../../shared/services/locale.service";
import {CalendarEventResource} from "../../../shared/types/calendar-types/calendarEventResource";
import {CalendarPrioritySumResource} from "../../../shared/types/calendar-types/calendarPrioritySumResource";
import {CalendarResource} from "../../../shared/types/calendar-types/calendarResource";
import {LanguageService} from "../../../shared/services/language.service";
import {CalendarEventPriority} from "../../../shared/types/calendar-types/calendarEventPriority";
import {TYPE} from "../../../shared/types/types";
import {mapCalendarEventResourceToSyncfusionEvent} from "../mapper/calendar-to-syncfusion.mapper";
import {CalendarEventUpdateRequest} from "../../../shared/types/calendar-types/calendarEventUpdateRequest";
import {createCalendarEventResourceSeriesOverrideEvent, mapCalendarEventResourceToCalendarEventUpdateRequest} from "../mapper/calendar-api.mapper";
import {ProfileService} from "../../../shared/services/profile.service";
import {CapabilitiesService} from "../../../shared/services/capabilities.service";
import {CalendarEventConfirmation} from "../../../shared/types/calendar-types/calendarEventConfirmation";
import {CalendarInventoryResource} from "../../../shared/types/calendar-types/calendarInventoryResource";
import {ToasterService} from "../../../shared/services/toaster.service";

interface SchedulerEventFilters {
  priorities: Record<CalendarEventPriority, boolean>,
  createdByMe: boolean,
  invitedToMe: boolean
}

interface SchedulerSidebarPreferences {
  selectedCalendarIds: (number | string)[] | null,
  priorities: Partial<Record<CalendarEventPriority, boolean>>,
  createdByMe?: boolean,
  invitedToMe?: boolean
}

export interface CalendarState {
  calendars: CalendarResource[],
  events: Map<(number | string), CalendarEventResource[]>,
  preLoadedMonths: Record<(number | string), string[]>,
  selectedCalendarID: number | string | null,
  selectedSchedulerCalendarIDs: (number | string)[],
  schedulerOptions: {
    selectedDate: Date,
    view: View,
    workHours: WorkHoursModel,
    workDays: number[],
    defaultNotificationTime: number,
  },
  widgetScheduler: {
    selectedDate: Date,
    upcomingEvents: Map<(number | string), CalendarEventResource[]>,
    weekEvents: Map<(number | string), Map<string, CalendarPrioritySumResource>>,
  },
  translate: boolean,
  calendarInventories: Map<(number | string), CalendarInventoryResource[]>,
  schedulerEventFilters: SchedulerEventFilters,
  pendingSchedulerSidebarPreferences: SchedulerSidebarPreferences | null
}

const initialState: CalendarState = {
  calendars: [],
  events: new Map<(number | string), CalendarEventResource[]>(),
  preLoadedMonths: {},
  selectedCalendarID: null,
  selectedSchedulerCalendarIDs: [],
  schedulerOptions: {
    selectedDate: new Date(),
    view: 'Day',
    workHours: {
      start: '00:00',
      end: '24:00',
      highlight: true,
    },
    workDays: [1, 2, 3, 4, 5],
    defaultNotificationTime: 5
  },
  widgetScheduler: {
    selectedDate: new Date(),
    upcomingEvents: new Map<(number | string), CalendarEventResource[]>(),
    weekEvents: new Map<(number | string), Map<string, CalendarPrioritySumResource>>(),
  },
  translate: false,
  calendarInventories: new Map<(number | string), CalendarInventoryResource[]>(),
  schedulerEventFilters: {
    priorities: {
      low: true,
      medium: true,
      high: true
    },
    createdByMe: true,
    invitedToMe: true
  },
  pendingSchedulerSidebarPreferences: null
};

const schedulerSidebarPreferencesStorageKey = 'calendar_scheduler_sidebar_preferences';

export const CalendarStore = signalStore(
  {providedIn: 'root'},
  withState(initialState),
  withDevtools('CalendarStore'),
  withMethods((store) => {
    const api: ApiService = inject(ApiService);
    const locale: LocaleService = inject(LocaleService);
    const profile: ProfileService = inject(ProfileService);
    const capabilities: CapabilitiesService = inject(CapabilitiesService);
    const lang: LanguageService = inject(LanguageService);
    const toaster: ToasterService = inject(ToasterService);

    return {
      // #region general
      initStore(){
        const selectedDateBeforeInit = store.schedulerOptions.selectedDate();
        this.resetStore();
        this.setSelectedDate(selectedDateBeforeInit);
        this.restoreSchedulerSidebarPreferences();
        capabilities.ensureUserCapabilitiesLoaded();
        if (!capabilities.capabilitiesLoaded()) {
          return;
        }
        if (!capabilities.hasCalendarRight()) {
          return;
        }
        this.loadCalendars();
        
        // Restore view from cookie
        const savedView = this.getViewFromCookie();
        if (savedView) {
          patchState(store, (state) => ({
            ...state,
            schedulerOptions: {
              ...state.schedulerOptions,
              view: savedView as View
            },
            translate: !!(profile.profile()?.user.auto_translation ?? false)
          }));
        }
      },
      resetStore() {
        const now = new Date();
        patchState(store, {
          ...initialState,
          schedulerOptions: {
            ...initialState.schedulerOptions,
            selectedDate: now,
          },
          widgetScheduler: {
            ...initialState.widgetScheduler,
            selectedDate: now,
            upcomingEvents: new Map<number, CalendarEventResource[]>(),
            weekEvents: new Map<number, Map<string, CalendarPrioritySumResource>>(),
          },
        });
      },
      setTranslate(translate: boolean){
        if(store.translate() !== translate) {
          patchState(store, (state) => 
            ({
              ...state,
              translate: translate
            })
          );
        }
      },
      // #endregion general
      // #region calendars
      loadCalendars(){
        api.getCalendars().pipe(
          take(1),
        ).subscribe({
          next: (calendars) => {
            this.setCalendars(calendars);
          },
          error: (error) => {
            console.error('Error loading calendar list: ', error);
          }
        })
      },
      setCalendars(calendars: CalendarResource[]){
        const pendingPreferences = store.pendingSchedulerSidebarPreferences();
        const allCalendarIds = calendars.map((calendar) => calendar.id);
        const sanitizedSelectedCalendarIds = pendingPreferences
          ? this.sanitizeSelectedCalendarIds(pendingPreferences.selectedCalendarIds, allCalendarIds)
          : allCalendarIds;
        const selectedSchedulerCalendarIDs = pendingPreferences && pendingPreferences.selectedCalendarIds !== null
          ? sanitizedSelectedCalendarIds
          : allCalendarIds;
        const selectedCalendarID = selectedSchedulerCalendarIDs[0] ?? calendars[0]?.id ?? null;

        patchState(store, (state) => 
        ({
          ...state,
          calendars: calendars,
          selectedCalendarID,
          selectedSchedulerCalendarIDs,
          pendingSchedulerSidebarPreferences: null
        })
        );
        if (pendingPreferences) {
          this.persistSchedulerSidebarPreferences();
        }
        this.loadCalendarInventory();
        this.evaluatePreLoading();
        this.evaluateWidgetUpcomingEvents();
        this.evaluateWidgetWeekEvents();
      },
      loadCalendarInventory() {
        store.calendars()
        .filter((x) => x.config.inventory_mode !== 'deny' && x.permission.fillable === true)
        .forEach((calendar) => {
          api.getCalendarInventoryList(calendar.id)
          .pipe(
            take(1),
          )
          .subscribe({
            next: (inventory) => {
              this.setCalendarInventory(calendar.id, inventory);
            },
            error: (error) => {
              console.error('Error loading calendar inventory list: ', error);
              }
            })
        })
      },
      setCalendarInventory(calendarId: number | string, inventory: CalendarInventoryResource[]) {
        const uniqueInventory = Array.from(
          new Map([...inventory, ...(store.calendarInventories().get(calendarId) ?? [])].map((item) => [item.id, item])).values()
        );

        patchState(store, (state) => {
          const updatedInventories = new Map(state.calendarInventories);
          updatedInventories.set(calendarId, uniqueInventory);
          return {
            ...state,
            calendarInventories: updatedInventories
          };
        });
      },
      setSelectedCalendarID(calendarID: number | string){
        patchState(store, (state) => 
        ({
          ...state,
          selectedCalendarID: calendarID
        })
        );
      },
      setSelectedSchedulerCalendarIDs(calendarIDs: (number | string)[], persist: boolean = true): void {
        const allCalendarIds = store.calendars().map(calendar => calendar.id);
        const normalizedCalendarIDs = calendarIDs.filter((calendarId, index, array) =>
          allCalendarIds.includes(calendarId) && array.indexOf(calendarId) === index
        );
        const currentCalendarIDs = store.selectedSchedulerCalendarIDs();
        const hasSameSelection = normalizedCalendarIDs.length === currentCalendarIDs.length
          && normalizedCalendarIDs.every((calendarId, index) => calendarId === currentCalendarIDs[index]);

        if (hasSameSelection) {
          return;
        }

        const fallbackCalendarID = normalizedCalendarIDs[0] ?? null;

        patchState(store, (state) =>
        ({
          ...state,
          selectedSchedulerCalendarIDs: normalizedCalendarIDs,
          selectedCalendarID: fallbackCalendarID
        })
        );
        if (persist) {
          this.persistSchedulerSidebarPreferences();
        }
        this.refreshVisibleSchedulerRangeForSelectedCalendars();
      },
      setSchedulerPriorityFilter(priority: CalendarEventPriority, checked: boolean, persist: boolean = true): void {
        if (store.schedulerEventFilters.priorities()[priority] === checked) {
          return;
        }

        patchState(store, (state) => ({
          ...state,
          schedulerEventFilters: {
            ...state.schedulerEventFilters,
            priorities: {
              ...state.schedulerEventFilters.priorities,
              [priority]: checked
            }
          }
        }));
        if (persist) {
          this.persistSchedulerSidebarPreferences();
        }
      },
      setSchedulerParticipationFilter(filterKey: 'createdByMe' | 'invitedToMe', checked: boolean, persist: boolean = true): void {
        if (store.schedulerEventFilters[filterKey]() === checked) {
          return;
        }

        patchState(store, (state) => ({
          ...state,
          schedulerEventFilters: {
            ...state.schedulerEventFilters,
            [filterKey]: checked
          }
        }));
        if (persist) {
          this.persistSchedulerSidebarPreferences();
        }
      },
      restoreSchedulerSidebarPreferences(): void {
        const preferences = this.readSchedulerSidebarPreferences();
        if (!preferences) {
          return;
        }

        patchState(store, (state) => ({
          ...state,
          schedulerEventFilters: {
            ...state.schedulerEventFilters,
            priorities: {
              low: typeof preferences.priorities.low === 'boolean' ? preferences.priorities.low : state.schedulerEventFilters.priorities.low,
              medium: typeof preferences.priorities.medium === 'boolean' ? preferences.priorities.medium : state.schedulerEventFilters.priorities.medium,
              high: typeof preferences.priorities.high === 'boolean' ? preferences.priorities.high : state.schedulerEventFilters.priorities.high
            },
            createdByMe: typeof preferences.createdByMe === 'boolean' ? preferences.createdByMe : state.schedulerEventFilters.createdByMe,
            invitedToMe: typeof preferences.invitedToMe === 'boolean' ? preferences.invitedToMe : state.schedulerEventFilters.invitedToMe
          },
          pendingSchedulerSidebarPreferences: preferences
        }));
      },
      persistSchedulerSidebarPreferences(): void {
        const preferences: SchedulerSidebarPreferences = {
          selectedCalendarIds: store.selectedSchedulerCalendarIDs(),
          priorities: {
            low: store.schedulerEventFilters.priorities().low,
            medium: store.schedulerEventFilters.priorities().medium,
            high: store.schedulerEventFilters.priorities().high
          },
          createdByMe: store.schedulerEventFilters.createdByMe(),
          invitedToMe: store.schedulerEventFilters.invitedToMe()
        };

        if (typeof localStorage === 'undefined') {
          return;
        }

        localStorage.setItem(schedulerSidebarPreferencesStorageKey, JSON.stringify(preferences));
      },
      readSchedulerSidebarPreferences(): SchedulerSidebarPreferences | null {
        if (typeof localStorage === 'undefined') {
          return null;
        }

        const rawPreferences = localStorage.getItem(schedulerSidebarPreferencesStorageKey);
        if (!rawPreferences) {
          return null;
        }

        try {
          const parsedPreferences = JSON.parse(rawPreferences) as Partial<SchedulerSidebarPreferences> | null;
          if (!parsedPreferences || typeof parsedPreferences !== 'object') {
            return null;
          }

          return {
            selectedCalendarIds: Array.isArray(parsedPreferences.selectedCalendarIds)
              ? parsedPreferences.selectedCalendarIds.filter((id) => typeof id === 'number' || typeof id === 'string')
              : null,
            priorities: typeof parsedPreferences.priorities === 'object' && parsedPreferences.priorities !== null
              ? parsedPreferences.priorities
              : {},
            createdByMe: parsedPreferences.createdByMe,
            invitedToMe: parsedPreferences.invitedToMe
          };
        } catch {
          return null;
        }
      },
      sanitizeSelectedCalendarIds(
        calendarIds: (number | string)[] | null,
        allCalendarIds: (number | string)[]
      ): (number | string)[] {
        if (calendarIds === null) {
          return allCalendarIds;
        }

        return calendarIds.filter((calendarId, index, array) =>
          allCalendarIds.includes(calendarId) && array.indexOf(calendarId) === index
        );
      },
      // #endregion calendars
      // #region events
      loadCalendarEvents(start: Date, end: Date){
        store.calendars().forEach((calendar) => {
          api.getCalendarEvents(calendar.id, start.toISOString(), end.toISOString())
          .pipe(
            take(1),
          )
          .subscribe({
            next: (events) => {
              this.updateEvents(calendar.id, events);
              this.updatePreLoadedMonths(calendar.id, start, end);
            },
            error: (error) => {
              console.error('Error loading calendar events: ', error);
              }
            })
      })
      },
      loadCalendarEventsForCalendars(calendarIds: (number | string)[], start: Date, end: Date): void {
        calendarIds.forEach((calendarId) => {
          api.getCalendarEvents(calendarId, start.toISOString(), end.toISOString())
          .pipe(
            take(1),
          )
          .subscribe({
            next: (events) => {
              this.updateEvents(calendarId, events);
              this.updatePreLoadedMonths(calendarId, start, end);
            },
            error: (error) => {
              console.error('Error loading calendar events: ', error);
              }
            })
        });
      },
      getVisibleSchedulerRange(): {start: Date, end: Date} {
        if(store.schedulerOptions.view() === 'Year') {
          return {
            start: getYearStartDate(store.schedulerOptions.selectedDate()),
            end: getYearEndDate(store.schedulerOptions.selectedDate())
          };
        }
        return {
          start: getPreviousMonthStartDate(store.schedulerOptions.selectedDate()),
          end: getNextMonthEndDate(store.schedulerOptions.selectedDate())
        };
      },
      getSelectedSchedulerCalendarIds(): (number | string)[] {
        const selectedCalendarIds = store.selectedSchedulerCalendarIDs();
        if (selectedCalendarIds.length > 0) {
          return selectedCalendarIds;
        }
        return store.calendars().map(calendar => calendar.id);
      },
      refreshVisibleSchedulerRangeForSelectedCalendars(): void {
        const calendarIds = this.getSelectedSchedulerCalendarIds();
        if (calendarIds.length === 0) {
          return;
        }
        const {start, end} = this.getVisibleSchedulerRange();
        this.loadCalendarEventsForCalendars(calendarIds, start, end);
      },
      setEvents(events: CalendarEventResource[], calendarId: number){
        patchState(store, (state) => {
          const updatedEvents = new Map(state.events);
          updatedEvents.set(calendarId, events);

          return {
            ...state,
            events: updatedEvents
          };
        });
      },
      updateEvents(calendarId: (number | string), events: CalendarEventResource[]) {
        const eventIds = new Set(events.map((e) => e.id));
        const stateEvents = (store.events().get(calendarId) ?? []).filter((event) => !eventIds.has(event.id));
        patchState(store, (state) => {
          const updatedEvents = new Map(state.events);
          updatedEvents.set(calendarId, [...stateEvents, ...events]);

          return {
            ...state,
            events: updatedEvents,
          };
        });
      },
      removeEvents(ids: number[], calendarId: number | string) {
        patchState(store, (state) => {
          const updatedEvents = new Map(state.events);
          updatedEvents.set(calendarId, (state.events.get(calendarId) ?? []).filter(event => !ids.includes(event.id)));

          return {
            ...state,
            events: updatedEvents
          };
        });
      },
      getEventById(id: number, calendarId: number | string): CalendarEventResource | undefined {
        return (store.events().get(calendarId) ?? []).find(event => event.id === id);
      },
      resolveEventWithSourceCalendar(
        id: number,
        preferredCalendarId?: number | string | null
      ): {event: CalendarEventResource; calendarId: number | string} | null {
        const resolveFromCalendar = (calendarId: number | string): {event: CalendarEventResource; calendarId: number | string} | null => {
          const event = (store.events().get(calendarId) ?? []).find((existingEvent) => existingEvent.id === id);
          if (!event) {
            return null;
          }

          const resolvedCalendarId = event.calendar_id ?? calendarId;
          return {
            event: {...event, calendar_id: resolvedCalendarId},
            calendarId: resolvedCalendarId
          };
        };

        if (preferredCalendarId !== null && preferredCalendarId !== undefined) {
          const preferredMatch = resolveFromCalendar(preferredCalendarId);
          if (preferredMatch) {
            return preferredMatch;
          }
        }

        for (const [calendarId] of store.events().entries()) {
          if (calendarId === preferredCalendarId) {
            continue;
          }

          const resolvedMatch = resolveFromCalendar(calendarId);
          if (resolvedMatch) {
            return resolvedMatch;
          }
        }

        return null;
      },
      filterEvents(calendarId: number, predicate: (e: CalendarEventResource) => boolean): CalendarEventResource[] {
        return (store.events().get(calendarId) ?? []).filter(predicate);
      },
      createEvent(event: CalendarEventUpdateRequest){
        api.createOrUpdateCalendarEvent(event, null, '', false, false, false, false)
        .pipe(
          take(1),
        )
        .subscribe({
          next: (events) => {
            this.updateEvents(event.calendar_id, events);
            this.evaluateWidgetWeekEvents();
            this.evaluateWidgetUpcomingEvents();
          },
          error: (error) => {
            console.error('Error creating calendar event: ', error);
            }
          })
      },
      createEventSeriesWithOverrides(event: CalendarEventUpdateRequest, overrideEvents: Date[]){
        api.createOrUpdateCalendarEvent(event, null, '', false, false, false, false)
        .pipe(
          take(1),
        )
        .subscribe({
          next: (events) => {
            overrideEvents.forEach((overrideDate) => {
              const updatedEvent = 
                createCalendarEventResourceSeriesOverrideEvent({...events[0]}, overrideDate);
              this.createEvent(mapCalendarEventResourceToCalendarEventUpdateRequest(updatedEvent));
            });
          },
          error: (error) => {
            console.error('Error creating calendar event: ', error);
            }
          })
      },
      updateEvent(event: CalendarEventUpdateRequest, eventId: number, overrideDetails: boolean, overrideGuests: boolean, overrideTiming: boolean, overrideInventories: boolean, previousCalendarId?: (number | string) ){
        const sourceCalendarId = previousCalendarId ?? event.calendar_id;
        const sourceEvent = (store.events().get(sourceCalendarId) ?? [])
          .find((existingEvent) => existingEvent.id === eventId);

        if (!sourceEvent) {
          console.error('Error updating calendar event: source event not found for update', {eventId, sourceCalendarId});
          return;
        }

        api.createOrUpdateCalendarEvent(event, eventId, sourceEvent.etag, overrideDetails, overrideGuests, overrideTiming, overrideInventories)
        .pipe(
          take(1),
        )
        .subscribe({
          next: (events) => {
            if (sourceCalendarId !== event.calendar_id) {
              const movedEventIds = Array.from(new Set([eventId, ...events.map((updatedEvent) => updatedEvent.id)]));
              this.removeEvents(movedEventIds, sourceCalendarId);
            }
            this.updateEvents(event.calendar_id, events);
            if (event.override.parent_id !== null) {
              this.refreshVisibleSchedulerRangeForSelectedCalendars();
            }
            this.evaluateWidgetWeekEvents();
            this.evaluateWidgetUpcomingEvents();
          },
          error: (error) => {
            console.error('Error updating calendar event: ', error);
            }
          })
      },
      deleteEvent(eventID: number, calendarId: (number | string)) {
        api.deleteCalendarEvent(eventID)
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.removeEvents([eventID, ... (store.events().get(calendarId) ?? []).filter(e => e.override.parent_id === eventID).map(e => e.id)], calendarId);
            this.evaluateWidgetWeekEvents();
            this.evaluateWidgetUpcomingEvents();
          },
          error: (error) => {
            console.error('Error deleting calendar event: ', error);
          }
        });
      },
      deleteOccurenceFromSeries(eventID: number, calendarId: (number | string), occurrenceDate: Date) {
        const eventToModify =  (store.events().get(calendarId) ?? []).find(event => event.id === eventID);
        if(eventToModify) {
          const updatedEvent = {...eventToModify};
          const timezoneHelper = new Timezone();
          const timezone = updatedEvent.timing.timezone || timezoneHelper.getLocalTimezoneName();
          const localizedOccurrenceDate = timezoneHelper.add(occurrenceDate, timezone);
          const year = localizedOccurrenceDate.getFullYear();
          const month = String(localizedOccurrenceDate.getMonth() + 1).padStart(2, '0');
          const day = String(localizedOccurrenceDate.getDate()).padStart(2, '0');
          updatedEvent.recurrence.deleted_occurrences = [
            ...updatedEvent.recurrence.deleted_occurrences,
            `${year}-${month}-${day}`
          ];
          this.updateEvent(mapCalendarEventResourceToCalendarEventUpdateRequest(updatedEvent), eventID, false, false, false, false);
        }
      },
      // #endregion events
      // #region preloading
      updatePreLoadedMonths(calendarId: number | string, start: Date, end: Date){
        const months: string[] = getMonthsBetweenDates(start, end)
        patchState(store, (state) => {
          const existingMonths = state.preLoadedMonths[calendarId] || [];
          const updatedMonths = [...existingMonths, ...months].filter((month, index, self) => 
            self.indexOf(month) === index
          );
          return {
            ...state,
            preLoadedMonths: {
              ...state.preLoadedMonths,
              [calendarId]: updatedMonths
            }
          };
        })
      },
      clearPreLoadedMonths(calendarId: number): void{
        if(store.preLoadedMonths()[calendarId]) {
          const updatedPreloadedMonths = {...store.preLoadedMonths()};
          delete updatedPreloadedMonths[calendarId];
          patchState(store, (state) => ({
            ...state,
            preLoadedMonths: updatedPreloadedMonths
          }));
        }
      },
      evaluatePreLoading(): void{
        const {start, end} = this.getVisibleSchedulerRange();
        const months = getMonthsBetweenDates(start, end);  
        
        // Check if all calendars have all required months loaded
        const allCalendarsLoaded = store.calendars().every((calendar) => {
          const preLoadedForCalendar = store.preLoadedMonths()[calendar.id] || [];
          return months.every((month) => preLoadedForCalendar.includes(month));
        });
        if (!allCalendarsLoaded) {
          // Check if some calendars have some months loaded to optimize loading
          const someLoaded = store.calendars().some((calendar) => {
            const preLoadedForCalendar = store.preLoadedMonths()[calendar.id] || [];
            return months.some((month) => preLoadedForCalendar.includes(month));
          });
          
          if (someLoaded) {
            // Find missing months across all calendars
            const allMissing = store.calendars().flatMap((calendar) => {
              const preLoadedForCalendar = store.preLoadedMonths()[calendar.id] || [];
              return months.filter(m => !preLoadedForCalendar.includes(m));
            });
            const uniqueMissing = [...new Set(allMissing)].sort();
            
            if (uniqueMissing.length > 0) {
              const firstMissing = new Date(uniqueMissing[0]);
              const lastMissing = new Date(uniqueMissing[uniqueMissing.length - 1]);
              this.loadCalendarEvents(firstMissing, getNextMonthEndDate(lastMissing));
            }
          } else {
            this.loadCalendarEvents(start, end);
          }
        }
      },
      checkForOutdatedEvent(calendarId: number, eventId: number, etag: string, isNewEvent: boolean): void {
        const event =  (store.events().get(calendarId) ?? []).find(e => e.id === eventId && e.calendar_id === calendarId);
        if(event && event.etag !== etag || isNewEvent) {
          this.clearPreLoadedMonths(calendarId);
          this.evaluatePreLoading();
        }
      },
      checkForEventToDelete(calendarId: number, eventId: number): void {
        const event = (store.events().get(calendarId) ?? []).find(e => e.id === eventId && e.calendar_id === calendarId);
        if(event) {
          this.removeEvents([eventId], calendarId);
        }
      },
      // #endregion preloading
      // #region scheduler options
      setSelectedDate(date: Date){
        patchState(store, (state) => 
        ({
          ...state,
          schedulerOptions: {
            ...state.schedulerOptions,
            selectedDate: date
          }
        })
        );
        this.evaluatePreLoading();
      },
      setView(view: View){
        patchState(store, (state) => 
        ({
          ...state,
          schedulerOptions: {
            ...state.schedulerOptions,
            view: view
          }
        })
        );

        // Save view to cookie
        this.saveViewToCookie(view);
        
        this.evaluatePreLoading();
      },
      saveViewToCookie(view: View): void {
        document.cookie = `calendar_view=${view}; path=/; max-age=31536000; Secure; SameSite=Strict`;
      },
      getViewFromCookie(): string | null {
        const match = document.cookie.match(/(^|;\s*)calendar_view=([^;]*)/);
        return match ? decodeURIComponent(match[2]) : null;
      },
      // #endregion scheduler options
      // #region widget scheduler
      initWidgetScheduler(){
        this.resetWidgetScheduler();
        capabilities.ensureUserCapabilitiesLoaded();
        if (!capabilities.capabilitiesLoaded()) {
          return;
        }
        if (!capabilities.hasCalendarRight()) {
          return;
        }
        this.loadCalendars();
        patchState(store, (state) => ({
          ...state,
          translate: !!(profile.profile()?.user.auto_translation ?? false)
        }));
      },
      widgetWeekStartDate(): Date {
        return getStartOfWeek(store.widgetScheduler.selectedDate(), locale.firstDayOfWeekIndex());
      },
      widgetGoToPreviousWeek(){
        const newDate = this.widgetWeekStartDate();
        newDate.setDate(newDate.getDate() - 7);
        this.setWidgetSelectedDate(newDate);
      },
      widgetGoToNextWeek(){
        const newDate = this.widgetWeekStartDate();
        newDate.setDate(newDate.getDate() + 7);
        this.setWidgetSelectedDate(newDate);
      },
      setWidgetSelectedDate(date: Date){
        patchState(store, (state) => 
        ({
          ...state,
          widgetScheduler: {
            ...state.widgetScheduler,
            selectedDate: date
          }
        })
        );
        this.evaluateWidgetWeekEvents();
        this.evaluateWidgetUpcomingEvents();
      },
      evaluateWidgetWeekEvents(){
        store.calendars()
        .filter((x) => x.type === 'user')
        .forEach((calendar) => {
          api.getCalendarDatePriorities(
            calendar.id,
            this.widgetWeekStartDate().toISOString(),
            getEndOfWeek(this.widgetWeekStartDate(), locale.firstDayOfWeekIndex()).toISOString()
          ).pipe(take(1)).subscribe({
            next: (priorities) => {
              this.setWidgetWeekEvents(calendar.id, new Map(Object.entries(priorities)));
            },
            error: (error) => {
              console.error('Error loading calendar events: ', error);
            }
          })
        });
      },
      setWidgetWeekEvents(calendarId: number | string, priorities: Map<(string | string), CalendarPrioritySumResource>){
        patchState(store, (state) => {
          const updatedWeekEvents = new Map(state.widgetScheduler.weekEvents);
          updatedWeekEvents.set(calendarId, priorities);

          return {
            ...state,
            widgetScheduler: {
              ...state.widgetScheduler,
              weekEvents: updatedWeekEvents
            }
          };
        });
      },
      evaluateWidgetUpcomingEvents(){
        store.calendars()
        .filter((x) => x.type === 'user')
        .forEach((calendar) => {
          api.getCalendarEventsForToday(calendar.id).pipe(take(1)).subscribe({
            next: (events) => {
              this.updateWidgetUpcomingEvents(calendar.id, events);
            },
            error: (error) => {
              console.error('Error loading calendar events: ', error);
            }
          })
        });
      },
      updateWidgetUpcomingEvents(calendarId: number | string, events: CalendarEventResource[]){
        patchState(store, (state) => {
          const updatedUpcomingEvents = new Map(state.widgetScheduler.upcomingEvents);
          updatedUpcomingEvents.set(calendarId, [...events]);

          return {
            ...state,
            widgetScheduler: {
              ...state.widgetScheduler,
              upcomingEvents: updatedUpcomingEvents
            }
          };
        });
      },
      resetWidgetScheduler(){
        patchState(store, (state) => ({
          ...state,
          widgetScheduler: {
            ...initialState.widgetScheduler,
            selectedDate: new Date(),
            upcomingEvents: new Map<number, CalendarEventResource[]>(),
            weekEvents: new Map<number, Map<string, CalendarPrioritySumResource>>()
          }
        }));
      },
      // #endregion widget scheduler
      // #region rsvp
      setRsvpAnswer(eventId: number, calendarId: (number | string | null), answer: CalendarEventConfirmation){
        api.setCalendarEventRSVP(eventId, {answer})
        .pipe(take(1))
        .subscribe({
          next: () => {
            toaster.success(
              lang.getTranslationByKey('SUCCESS'),
              lang.getTranslationByKey('CALRESPONSESETSUCCESS')
            );
            if(answer === CalendarEventConfirmation.No) {
              if(calendarId !== null) {
                this.removeEvents([eventId], calendarId);
              }
              this.refreshVisibleSchedulerRangeForSelectedCalendars();
            }
          }
        })
      }
      // #endregion rsvp
    }
  }),
  withComputed((store) => {
    const language: LanguageService = inject(LanguageService);
    const locale: LocaleService = inject(LocaleService);
    const profile: ProfileService = inject(ProfileService);

    return {
      // #region scheduler
      schedulerCalendars: computed(() => {
        const mappedData = store.calendars().map((calendar) => {
          return {
            CalendarText: calendar.name || 'Calendar',
            CalendarId: calendar.id.toString(),
            CalendarColor: '#c43081',
          }
        });
        return mappedData;
      }),
      schedulerEventSettings: computed((): EventSettingsModel => {
        const selectedCalendarIds = store.selectedSchedulerCalendarIDs();
        const currentUserId = profile.profile()?.user.id ?? null;
        const currentUserEmail = profile.profile()?.user.email?.toLowerCase() ?? null;
        const currentUserGroupIds = new Set(profile.profile()?.groups.map((group) => group.id) ?? []);
        const priorityFilters = store.schedulerEventFilters.priorities();
        const showCreatedByMe = store.schedulerEventFilters.createdByMe();
        const showInvitedToMe = store.schedulerEventFilters.invitedToMe();
        const seenEventIds = new Set<number | string>();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const displayEvents: any[] = selectedCalendarIds.flatMap((id) =>
          (store.events().get(id) ?? [])
          .filter((event) => {
            const priority = event.priority;
            const priorityVisible = priority === null ? true : (priorityFilters[priority] ?? true);
            if (!priorityVisible) {
              return false;
            }

            if (currentUserId === null) {
              return true;
            }

            if (event.permission.masked) {
              return true;
            }

            const createdByMe = event.creator.user_id === currentUserId;
            if (createdByMe) {
              return showCreatedByMe;
            }

            const invitedToCurrentUser = isEventInvitingCurrentUser(event, currentUserId, currentUserEmail, currentUserGroupIds);
            if (!invitedToCurrentUser) {
              return false;
            }

            if (!showInvitedToMe) {
              return false;
            }

            return true;
          })
          .map((event) =>
            mapCalendarEventResourceToSyncfusionEvent(event, id, store.translate(), language.getTranslationByKey('MASKED'))
          )
          .filter((event) => {
            if (seenEventIds.has(event.Id)) {
              return false;
            }

            seenEventIds.add(event.Id);
            return true;
          })
        );
        return {
          dataSource: displayEvents,
          fields: {
            id: 'Id',
            subject: {name: 'Subject'},
            startTime: {name: 'StartTime'},
            endTime: {name: 'EndTime'},
            startTimezone: {name: 'StartTimezone'},
            endTimezone: {name: 'EndTimezone'},
            location: {name: 'Location'},
            description: {name: 'Description'},
            recurrenceRule: {name: 'RecurrenceRule'},
            recurrenceException: {name: 'RecurrenceException'},
            recurrenceID: {name: 'RecurrenceID'},
          }
        };
      }),
      schedulerSelectionOrFiltersActive: computed(() => {
        const calendars = store.calendars();
        const selectedCalendarIds = store.selectedSchedulerCalendarIDs();
        const priorities = store.schedulerEventFilters.priorities();
        const currentUserId = profile.profile()?.user.id ?? null;
        const personalCalendar = currentUserId === null
          ? null
          : (calendars.find((calendar) => calendar.type === 'user' && calendar.owner.id === currentUserId) ?? null);

        const personalCalendarHidden = personalCalendar !== null && !selectedCalendarIds.includes(personalCalendar.id);
        const filtersActive = !priorities.low
          || !priorities.medium
          || !priorities.high
          || !store.schedulerEventFilters.createdByMe()
          || !store.schedulerEventFilters.invitedToMe();

        return personalCalendarHidden || filtersActive;
      }),
      // #endregion scheduler
      // #region widget
      widgetSchedulerEvents: linkedSignal((): TYPE.WidgetCalendarEvent[] => {
        const start = getStartOfWeek(store.widgetScheduler.selectedDate(), locale.firstDayOfWeekIndex());
        const mergedWeekEvents = new Map<string, CalendarPrioritySumResource>();
        store.widgetScheduler.weekEvents().forEach((calendarWeekEvents) => {
          calendarWeekEvents.forEach((prioritySum, dateKey) => {
            const existing = mergedWeekEvents.get(dateKey) ?? {low: 0, medium: 0, high: 0};
            mergedWeekEvents.set(dateKey, {
              low: existing.low + prioritySum.low,
              medium: existing.medium + prioritySum.medium,
              high: existing.high + prioritySum.high,
            });
          });
        });
        const mappedData: TYPE.WidgetCalendarEvent[] = [];

        for (let i = 0; i < 7; i++) {
          const dayDate = new Date(start);
          dayDate.setDate(start.getDate() + i);

          const label = dayDate.toLocaleDateString(language.getLanguage(), {weekday: 'short'});
          const day = dayDate.getDate();

          const dateKey = dayDate.getFullYear() + '-' + String(dayDate.getMonth() + 1).padStart(2, '0') + '-' + String(dayDate.getDate()).padStart(2, '0');
          
          const event = mergedWeekEvents.get(dateKey) || {low: 0, medium: 0, high: 0};

          const eventDots: CalendarEventPriority[] = [];
          for (let i = 0; i < event.high; i++) {
            eventDots.push('high');
          }
          for (let i = 0; i < event.medium; i++) {
            eventDots.push('medium');
          }
          for (let i = 0; i < event.low; i++) {
            eventDots.push('low');
          }
          
          mappedData.push({
            date: dayDate,
            label,
            day,
            event,
            eventDots: eventDots.slice(0, 3)
          });
        }

        return mappedData;
      }),
      widgetUpcomingEvents: linkedSignal((): CalendarEventResource[] => {
        return Array.from(store.widgetScheduler.upcomingEvents().values())
          .flat()
          // for sorting we need to look only at the time and ignore the date
          .sort((a, b) => {
            const aStartAt = a.timing.start_at;
            const bStartAt = b.timing.start_at;
            const aTime = ((aStartAt.charCodeAt(11) - 48) * 10 + (aStartAt.charCodeAt(12) - 48)) * 60
              + (aStartAt.charCodeAt(14) - 48) * 10
              + (aStartAt.charCodeAt(15) - 48);
            const bTime = ((bStartAt.charCodeAt(11) - 48) * 10 + (bStartAt.charCodeAt(12) - 48)) * 60
              + (bStartAt.charCodeAt(14) - 48) * 10
              + (bStartAt.charCodeAt(15) - 48);

            return aTime - bTime;
          });
      }),
      widgetSchedulerMonth: computed(() => {
        return store.widgetScheduler.selectedDate().toLocaleDateString(language.getLanguage(), {month: 'short'});
      })
      // #endregion widget
    }
  })
);

function isEventInvitingCurrentUser(
  event: CalendarEventResource,
  currentUserId: number | null,
  currentUserEmail: string | null,
  currentUserGroupIds: Set<number>
): boolean {
  if (currentUserId === null) {
    return false;
  }

  if (event.creator.user_id === currentUserId) {
    return false;
  }

  if (event.participants.user_ids.includes(currentUserId)) {
    return true;
  }

  if (currentUserEmail && event.participants.external_user_emails.some((email) => email.toLowerCase() === currentUserEmail)) {
    return true;
  }

  return event.participants.group_ids.some((groupId) => currentUserGroupIds.has(groupId));
}
